/**
 * The import-definition designer (TO-400, Epic 25 F12): inspect a sample,
 * preview a contract over it and dry-run it against current data — all on the
 * real F6–F9 engine, synchronously, with nothing stored.
 */
import type { ImportDefinitionContract, ImportFormat } from './import-definition.js'
import type { CanonicalValues } from './import-processing.js'
import type {
	ImportValidationError,
	ImportValidationOutcome,
	ImportValidationStage,
	ImportValidationStageResult,
	ImportValidationTotals,
} from './import-validation.js'

/** The designer's own 422 codes (spec Contract); contract rules reuse `IMPORT_DEFINITION_INVALID`. */
export enum ImportDesignerErrorCode {
	/** The body breaks its shape or sample rules; `details.fields` lists them (BR-1). */
	REQUEST_INVALID = 'IMPORT_DESIGNER_REQUEST_INVALID',
	/** A dry-run targets a canonical schema with no registered importer; `details.canonicalSchema`. */
	NO_IMPORTER = 'IMPORT_DESIGNER_NO_IMPORTER',
}

/** Decoded sample bytes at most (spec BR-1). */
export const IMPORT_DESIGNER_MAX_SAMPLE_BYTES = 1_048_576
/** A designer request body at most, in bytes (spec BR-1 → 413). */
export const IMPORT_DESIGNER_MAX_BODY_BYTES = 1_500_000
/** The preview `limit` range and default (spec BR-8). */
export const IMPORT_DESIGNER_PREVIEW_LIMIT = { min: 1, max: 100, default: 20 } as const

/** A sample file, base64 in the body; never stored (spec BR-1, BR-2). */
export interface ImportDesignerSample {
	filename: string
	contentBase64: string
}

export interface ImportDesignerInspectRequest {
	sample: ImportDesignerSample
}

export interface ImportDesignerPreviewRequest {
	contract: ImportDefinitionContract
	sample: ImportDesignerSample
	limit?: number
}

export interface ImportDesignerDryRunRequest {
	contract: ImportDefinitionContract
	sample: ImportDesignerSample
}

/** `POST …/inspect` (spec BR-3…BR-7). */
export interface ImportDesignerInspection {
	format: ImportFormat
	/** The first ≤ 50 decoded lines, each ≤ 2 000 code points. */
	lines: string[]
	/** Fixed-width only: the longest of `lines`; `null` otherwise. */
	lineLength: number | null
}

/** One previewed record: its canonical values when accepted, and its errors. */
export interface ImportDesignerPreviewRow {
	recordNumber: number
	values: CanonicalValues | null
	errors: ImportValidationError[]
}

/** The pipeline's verdict, shared by preview and dry-run. */
export interface ImportDesignerValidation {
	outcome: ImportValidationOutcome
	failedStage: ImportValidationStage | null
	stages: ImportValidationStageResult[]
	totals: ImportValidationTotals
}

/** `POST …/preview` (spec BR-8). */
export interface ImportDesignerPreview extends ImportDesignerValidation {
	/** Record numbers 1…limit the loader produced, ascending. */
	rows: ImportDesignerPreviewRow[]
	/** The errors with no record number, at most 100. */
	fileErrors: ImportValidationError[]
}

/** The planned counts of a dry-run (spec BR-9). */
export interface ImportDesignerPlan {
	total: number
	created: number
	updated: number
	unchanged: number
	deactivated: number
	invalid: number
	/** The plan has an INVALID entry and `onError` is `reject-file`. */
	wouldRejectFile: boolean
}

/** `POST …/dry-run`: `plan` is `null` when validation is INVALID. */
export interface ImportDesignerDryRun {
	validation: ImportDesignerValidation
	plan: ImportDesignerPlan | null
}
